import * as React from 'react';
import * as styles from "./CalendarView.css";
import { inject, observer } from 'mobx-react';
import { MessagesStore } from '../stores';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import daLocale from '@fullcalendar/core/locales/da';
import svLocale from '@fullcalendar/core/locales/sv';
import nbLocale from '@fullcalendar/core/locales/nb';
import deLocale from '@fullcalendar/core/locales/de';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { EventSourceInput, LocaleInput } from '@fullcalendar/core';
import { Helper } from '../../../Helper';
import { IAButton } from '../../button/Button';
import { LocalizationService } from '../../../services/LocalizationService';
import { IAIconDropDown } from '../../iconDropDown/IconDropDown';
import { IEventPeriod } from '../../../interfaces/IEventPeriod';
import { IASpinner } from '../../spinner/Spinner';
import { IASkeleton } from '../../skeleton/Skeleton';

export interface ICalendarViewProps {
  messagesStore?: MessagesStore;
  getMessages?: () => void;
}

export interface ICalendarViewState {
  title: string;
  isWorkWeek?: boolean;
}

@inject('messagesStore')
@observer
export default class CalendarView extends React.Component<ICalendarViewProps, ICalendarViewState> {
  private calendar: FullCalendar;
  private datePicker: HTMLInputElement;
  private readonly localizationService = new LocalizationService();
  private readonly localStorageKey = "IA_calendarView";

  private readonly viewOptions = [
    {
      value: "day",
      label: this.localizationService.strings.Events_Day ?? "Day",
      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Calendar%20Day/SVG/ic_fluent_calendar_day_28_regular.svg",
      iconColor: this.props.messagesStore.highlightColor
    },
    {
      value: "workWeek",
      label: this.localizationService.strings.Event_WorkWeek ?? "Work week",
      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Calendar%20Work%20Week/SVG/ic_fluent_calendar_work_week_28_regular.svg",
      iconColor: this.props.messagesStore.highlightColor
    },
    {
      value: "week",
      label: this.localizationService.strings.Events_Week ?? "Week",
      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Calendar%20Work%20Week/SVG/ic_fluent_calendar_work_week_28_regular.svg",
      iconColor: this.props.messagesStore.highlightColor
    },
    {
      value: "month",
      label: this.localizationService.strings.Events_Month ?? "Month",
      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Calendar%20Month/SVG/ic_fluent_calendar_month_32_regular.svg",
      iconColor: this.props.messagesStore.highlightColor
    }
  ];

  constructor(props) {
    super(props);
    this.state = {
      title: "",
      isWorkWeek: false
    }
  }

  componentDidMount(): void {
    this.setTitle();
    const storedView = localStorage.getItem(this.localStorageKey);
    if (storedView) {
      this.setPeriod(storedView as IEventPeriod | "workWeek");
      this.fetchEventsFromPeriod();
      this.setTitle();
    }
  }

  componentDidUpdate(): void {
    this.calendar?.getApi().updateSize();
  }

  private setPeriod(period: IEventPeriod | "workWeek") {
    const isWorkWeek = period === "workWeek";
    this.setState({ isWorkWeek });
    if (period === this.props.messagesStore.showPeriod) {
      return;
    }
    const api = this.calendar.getApi();
    switch (period) {
      case "day":
        api.changeView("timeGridDay", new Date());
        break;
      case "workWeek":
      case "week":
        api.changeView("timeGridWeek", new Date());
        break;
      case "month":
        api.changeView("dayGridMonth", new Date());
        break;
    }
    this.props.messagesStore.showPeriod = period === "workWeek" ? "week" : period;
  }

  private fetchEventsFromPeriod() {
    if (this.props.messagesStore.isFetchingMessages) {
      return;
    }
    const api = this.calendar.getApi();
    this.props.messagesStore.startDate = api.view.activeStart;
    this.props.messagesStore.endDate = api.view.activeEnd;
    this.props.getMessages();
  }

  private parseEvents(): EventSourceInput {
    return this.props.messagesStore.messages?.map((event) => {
      const categoryBackgroundColor = event.categories?.length > 0 ? event.categories[0].bgColor : undefined;
      const categoryTextColor = event.categories?.length > 0 ? event.categories[0].textColor : undefined;
      const id = `${event.id}_${event.startDate}`;
      return {
        id: id,
        title: event.title,
        start: event.startDate,
        end: event.endDate,
        allDay: event.allDayEvent,
        
        extendedProps: event,
        backgroundColor: categoryBackgroundColor,
        textColor: categoryTextColor,
        borderColor: categoryBackgroundColor,
        classNames: [styles.IA_CalendarViewEvent],
      };
    });
  }

  private generateCSSVariables(): React.CSSProperties {
    const backgroundColor = this.props.messagesStore.highlightColor;
    const textColor = Helper.getTextColorBasedOnBackgroundColorDarkness(backgroundColor, "#ffffff", "#000000");
    const hoverColor = Helper.lightenOrDarkenColor(backgroundColor);
    const highlightColorWithOpacity = Helper.getColorWithOpacity(backgroundColor, 0.15, "rgba(255, 220, 40, 0.15)");
    return {
      ["--fc-button-bg-color" as any]: backgroundColor,
      ["--fc-button-border-color" as any]: backgroundColor,
      ["--fc-button-text-color" as any]: textColor,
      ["--fc-event-bg-color" as any]: backgroundColor,
      ["--fc-event-border-color" as any]: backgroundColor,
      ["--fc-event-text-color" as any]: textColor,
      ["--fc-button-hover-bg-color" as any]: hoverColor,
      ["--fc-button-hover-border-color" as any]: hoverColor,
      ["--fc-button-active-bg-color" as any]: backgroundColor,
      ["--fc-button-active-border-color" as any]: backgroundColor,
      ["--fc-today-bg-color" as any]: highlightColorWithOpacity,
    }
  }

  private setTitle() {
    const api = this.calendar?.getApi();
    let viewStartDate = new Date();
    if (api) {
      viewStartDate = api.view.currentStart;
    }
    const languageCode = this.localizationService.getLanguageCode();
    switch (this.props.messagesStore.showPeriod) {
      case "day":
        // Example: 1 April 2025
        this.setState({ title: viewStartDate.toLocaleString(languageCode, { month: "long", day: "numeric", year: "numeric" }) });
        break;
      case "week":
        // Example: April 2025
        // If the week is in two different months, show both months
        // Example: April - May 2025
        const oneWeekFromNow = new Date(viewStartDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        if (viewStartDate.getMonth() !== oneWeekFromNow.getMonth()) {
          this.setState({ title: `${viewStartDate.toLocaleString(languageCode, { month: "long" })} - ${oneWeekFromNow.toLocaleString(languageCode, { month: "long", year: "numeric" })}` });
          break;
        }
        this.setState({ title: viewStartDate.toLocaleString(languageCode, { month: "long", year: "numeric" }) });
        break;
      case "month":
        // Example: April 2025
        this.setState({ title: viewStartDate.toLocaleString(languageCode, { month: "long", year: "numeric" }) });
        break;
    }
  }

  private getLocalization() {
    const locale = this.localizationService.getLanguageFromNavigator();
    let calendarLocale: LocaleInput;
    switch (locale) {
      case "Danish":
        calendarLocale = daLocale;
        calendarLocale.weekTextLong = "Uge";
        break;
      case "Swedish":
        calendarLocale = svLocale;
        break;
      case "German":
        calendarLocale = deLocale;
        break;
      case "Norwegian":
        calendarLocale = nbLocale
        calendarLocale.weekText = "U";
        break;
      default:
        calendarLocale = enLocale;
        break;
    }
    calendarLocale.allDayContent = "";
    calendarLocale.weekText = "";
    return calendarLocale;
  }

  private getMouseOverButtonColor() {
    if (this.props.messagesStore.isFetchingMessages) {
      return undefined;
    }
    return this.props.messagesStore.hasDarkBackground ? "#ffffff12" : "#00000012";
  }

  private isLoadingWithNoEvents(): boolean {
    const store = this.props.messagesStore;
    const hasNoEvents = !store.messages || store.messages.length === 0;
    const isNotFiltering = store.searchText === "" && !store.filterData.isFiltered;
    return store.loading || (store.isFetchingMessages && hasNoEvents && isNotFiltering);
  }

  render(): JSX.Element {
    const view = this.props.messagesStore.showPeriod === "day" ? "timeGridDay"
      : this.props.messagesStore.showPeriod === "week" ? "timeGridWeek"
      : "dayGridMonth";
    const showSkeleton = this.isLoadingWithNoEvents();
    return <div
      className={styles.IA_CalendarView}
      style={{
        ...this.generateCSSVariables(),
        fontSize: this.props.messagesStore.isAdminPage ? 16 : undefined,
        fontWeight: this.props.messagesStore.isAdminPage ? 800 : undefined
      }}
    >

      {/* Toolbar */}
      <div
        className={styles.IA_CalendarViewToolbarWrapper}
        style={{
          fontWeight: this.props.messagesStore.isAdminPage ? 600 : undefined
        }}
      >
        <div className={styles.IA_CalendarViewToolbarSectionLeft}>
          <IAButton
            buttonColor={"transparent"}
            mouseOverButtonColor={this.getMouseOverButtonColor()}
            iconColor={this.props.messagesStore.hasDarkBackground ? "white" : this.props.messagesStore.highlightColor}
            textColor={this.props.messagesStore.hasDarkBackground ? "white" : undefined}
            borderRadius={5}
            label={this.localizationService.strings.Events_Today}
            iconSize={22}
            style={{
              padding: "0 8px",
              fontSize: 14,
              marginRight: 10,
            }}
            iconMarginTop={4}
            disbaled={this.props.messagesStore.isFetchingMessages}
            iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Calendar%20Today/SVG/ic_fluent_calendar_today_28_regular.svg"
            onClick={() => {
              const api = this.calendar.getApi();
              api.today();
              this.setTitle();
              this.fetchEventsFromPeriod();
            }}
          />
          <IAButton
            buttonColor={"transparent"}
            mouseOverButtonColor={this.getMouseOverButtonColor()}
            iconColor={this.props.messagesStore.hasDarkBackground ? "white" : this.props.messagesStore.highlightColor}
            borderRadius={500}
            style={{
              paddingLeft: 2
            }}
            iconMarginTop={4}
            iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/iOS%20Arrow%20LTR/SVG/ic_fluent_ios_arrow_ltr_24_regular.svg"
            disbaled={this.props.messagesStore.isFetchingMessages}
            onClick={() => {
              const api = this.calendar.getApi();
              api.prev();
              this.setTitle();
              this.fetchEventsFromPeriod();
            }}
          />
          <IAButton
            buttonColor={"transparent"}
            mouseOverButtonColor={this.getMouseOverButtonColor()}
            iconColor={this.props.messagesStore.hasDarkBackground ? "white" : this.props.messagesStore.highlightColor}
            borderRadius={500}
            style={{
              paddingRight: 2,
              marginRight: 10,
            }}
            iconMarginTop={4}
            iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/iOS%20Arrow%20RTL/SVG/ic_fluent_ios_arrow_rtl_24_regular.svg"
            disbaled={this.props.messagesStore.isFetchingMessages}
            onClick={() => {
              const api = this.calendar.getApi();
              api.next();
              this.setTitle();
              this.fetchEventsFromPeriod();
            }}
          />
          <div className={styles.IA_CalendarViewTitleButtonWrapper}>
            <input
              type={"date"}
              ref={ref => this.datePicker = ref}
              disabled={this.props.messagesStore.isFetchingMessages}
              onChange={(input) => {
                if (input.target.value) {
                  const api = this.calendar.getApi();
                  const date = new Date(input.target.value);
                  api.gotoDate(date);
                  this.setTitle();
                  this.fetchEventsFromPeriod();
                }
              }}
            />
            <IAButton
              buttonColor={"transparent"}
              mouseOverButtonColor={this.getMouseOverButtonColor()}
              iconColor={this.props.messagesStore.hasDarkBackground ? "white" : this.props.messagesStore.highlightColor}
              textColor={this.props.messagesStore.hasDarkBackground ? "white" : undefined}
              borderRadius={5}
              label={this.state.title}
              iconSize={18}
              style={{
                padding: "0 8px",
                fontSize: 14,
                marginRight: 10,
              }}
              iconMarginTop={7}
              iconMarginLeft={5}
              iconMarginRight={-4}
              disbaled={this.props.messagesStore.isFetchingMessages}
              iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg"
              showIconOnRight
              onClick={() => {
                // @ts-ignore
                this.datePicker?.showPicker();
              }}
            />
          </div>
        </div>
        <div className={styles.IA_CalendarViewToolbarSectionRight}>
          {this.props.messagesStore.isFetchingMessages &&
            <IASpinner
              color={this.props.messagesStore.hasDarkBackground ? "white" : this.props.messagesStore.highlightColor}
              style={{
                height: 16,
                width: 16,
                margin: "8px 0 0 0"
              }}
            />
          }
          <IAIconDropDown
            onChange={(selected) => {
              this.setPeriod(selected.value as IEventPeriod | "workWeek");
              this.fetchEventsFromPeriod();
              this.setTitle();
              localStorage.setItem(this.localStorageKey, selected.value);
            }}
            selectedOption={this.state.isWorkWeek
              ? this.viewOptions.find(option => option.value === "workWeek")
              : this.viewOptions.find(option => option.value === this.props.messagesStore.showPeriod)}
            options={this.viewOptions}
            style={{
              zIndex: 4,
              minWidth: 175,
              fontSize: 14
            }}
            highlightColor={this.props.messagesStore.highlightColor}
            disabled={this.props.messagesStore.isFetchingMessages}
          />
        </div>
      </div>

      {/* Calendar */}
      <div className={styles.IA_CalendarViewCalendarWrapper}>
        <div style={{ visibility: showSkeleton ? "hidden" : undefined }}>
          <FullCalendar
            ref={ref => this.calendar = ref}
            plugins={[ dayGridPlugin, timeGridPlugin ]}
            initialView={view}
            headerToolbar={{
              start: "",
              end: ""
            }}
            events={this.parseEvents()}
            eventClick={event => {
              this.props.messagesStore.openMessage(event.event.extendedProps);
            }}
            firstDay={1}
            eventTimeFormat={{
              hour: "numeric",
              minute: "2-digit",
              meridiem: false,
              hour12: false,
            }}
            locale={this.getLocalization()}
            weekNumbers
            height={this.props.messagesStore.calendarHeight ?? "auto"}
            nowIndicator={true}
            weekends={!this.state.isWorkWeek}
            businessHours
          />
        </div>
        {showSkeleton &&
          <div className={styles.IA_CalendarViewSkeleton}>
            <IASkeleton skeleton={{ height: "100%", width: "100%", borderRadius: 5 }} />
          </div>
        }
      </div>
    </div>
  }
}